import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Divider,
  Chip,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PaymentIcon from '@mui/icons-material/Payment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import axios from 'axios';

const API_BASE_URL = 'https://api.goldfroeschli-kinderwelt.ch/api';

const PaymentSettings = () => {
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSecrets, setShowSecrets] = useState({});

  useEffect(() => {
    loadGateways();
  }, []);

  const loadGateways = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Get list of gateways
      const listResponse = await axios.get(`${API_BASE_URL}/payments/list_gateways.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (listResponse.data.success) {
        // For each gateway, fetch full details including API keys
        const gatewaysWithDetails = await Promise.all(
          listResponse.data.data.map(async (gateway) => {
            try {
              const detailResponse = await axios.get(
                `${API_BASE_URL}/payments/get_gateway.php?gateway_name=${gateway.gateway_name}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              return detailResponse.data.success ? detailResponse.data.data : gateway;
            } catch (err) {
              return gateway;
            }
          })
        );
        
        setGateways(gatewaysWithDetails);
      } else {
        setError(listResponse.data.message);
      }
    } catch (err) {
      setError('Fehler beim Laden der Zahlungsgateways: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (gatewayName, field, value) => {
    setGateways(gateways.map(gateway => 
      gateway.gateway_name === gatewayName
        ? { ...gateway, [field]: value }
        : gateway
    ));
  };

  const handleSave = async (gateway) => {
    setSaving({ ...saving, [gateway.gateway_name]: true });
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/payments/update_gateway.php`,
        {
          gateway_name: gateway.gateway_name,
          is_active: gateway.is_active,
          is_test_mode: gateway.is_test_mode,
          api_key_live: gateway.api_key_live,
          api_secret_live: gateway.api_secret_live,
          api_key_test: gateway.api_key_test,
          api_secret_test: gateway.api_secret_test,
          description: gateway.description,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess(`${gateway.display_name} erfolgreich aktualisiert`);
        loadGateways();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError('Fehler beim Speichern: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving({ ...saving, [gateway.gateway_name]: false });
    }
  };

  const toggleShowSecret = (key) => {
    setShowSecrets({
      ...showSecrets,
      [key]: !showSecrets[key]
    });
  };

  const getGatewayIcon = (gatewayName) => {
    switch (gatewayName) {
      case 'stripe':
        return '💳';
      case 'twint':
        return '📱';
      default:
        return '💰';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 900 }}>
        Zahlungseinstellungen
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Konfigurieren Sie Ihre Payment-Gateways. Wenn mindestens ein Gateway aktiv ist, müssen Kunden vor der Buchung bezahlen.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {gateways.map((gateway) => (
          <Grid item xs={12} key={gateway.gateway_name}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h3">{getGatewayIcon(gateway.gateway_name)}</Typography>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {gateway.display_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {gateway.description || `${gateway.display_name} Zahlungsgateway`}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {gateway.is_test_mode ? (
                      <Chip label="Test Modus" color="warning" size="small" />
                    ) : (
                      <Chip label="Live Modus" color="success" size="small" />
                    )}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(gateway.is_active)}
                          onChange={(e) => handleChange(gateway.gateway_name, 'is_active', e.target.checked)}
                          color="primary"
                        />
                      }
                      label={gateway.is_active ? 'Aktiv' : 'Inaktiv'}
                    />
                  </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  {/* Test Mode Toggle */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(gateway.is_test_mode)}
                          onChange={(e) => handleChange(gateway.gateway_name, 'is_test_mode', e.target.checked)}
                          color="warning"
                        />
                      }
                      label="Test Modus (Sandbox)"
                    />
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4 }}>
                      Im Test Modus werden keine echten Zahlungen verarbeitet
                    </Typography>
                  </Grid>

                  {/* Test Mode Credentials */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Test API Credentials (Sandbox)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Test API Key / Public Key"
                          value={gateway.api_key_test || ''}
                          onChange={(e) => handleChange(gateway.gateway_name, 'api_key_test', e.target.value)}
                          placeholder={gateway.gateway_name === 'stripe' ? 'pk_test_...' : 'Test Key'}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Test Secret Key"
                          type={showSecrets[gateway.gateway_name + '_test'] ? 'text' : 'password'}
                          value={gateway.api_secret_test || ''}
                          onChange={(e) => handleChange(gateway.gateway_name, 'api_secret_test', e.target.value)}
                          placeholder={gateway.gateway_name === 'stripe' ? 'sk_test_...' : 'Test Secret'}
                          size="small"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => toggleShowSecret(gateway.gateway_name + '_test')}
                                  edge="end"
                                  size="small"
                                >
                                  {showSecrets[gateway.gateway_name + '_test'] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Live Mode Credentials */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'error.main' }}>
                      Live API Credentials (Produktion) ⚠️
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Live API Key / Public Key"
                          value={gateway.api_key_live || ''}
                          onChange={(e) => handleChange(gateway.gateway_name, 'api_key_live', e.target.value)}
                          placeholder={gateway.gateway_name === 'stripe' ? 'pk_live_...' : 'Live Key'}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Live Secret Key"
                          type={showSecrets[gateway.gateway_name + '_live'] ? 'text' : 'password'}
                          value={gateway.api_secret_live || ''}
                          onChange={(e) => handleChange(gateway.gateway_name, 'api_secret_live', e.target.value)}
                          placeholder={gateway.gateway_name === 'stripe' ? 'sk_live_...' : 'Live Secret'}
                          size="small"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => toggleShowSecret(gateway.gateway_name + '_live')}
                                  edge="end"
                                  size="small"
                                >
                                  {showSecrets[gateway.gateway_name + '_live'] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Gateway Specific Settings */}
                  {gateway.gateway_name === 'stripe' && (
                    <Grid item xs={12}>
                      <Alert severity="info" icon={<PaymentIcon />}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Stripe Einrichtung:
                        </Typography>
                        <Typography variant="caption" component="div">
                          1. Erstellen Sie ein Konto bei <a href="https://stripe.com" target="_blank" rel="noopener">stripe.com</a><br />
                          2. Gehen Sie zu Entwickler → API-Schlüssel<br />
                          3. Kopieren Sie den Publishable Key (pk_...) und Secret Key (sk_...)<br />
                          4. Für Produktion: Aktivieren Sie Ihr Konto und verwenden Sie Live Keys
                        </Typography>
                      </Alert>
                    </Grid>
                  )}

                  {gateway.gateway_name === 'twint' && (
                    <Grid item xs={12}>
                      <Alert severity="info" icon={<PaymentIcon />}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          TWINT Einrichtung:
                        </Typography>
                        <Typography variant="caption" component="div">
                          1. Registrieren Sie sich als TWINT Händler<br />
                          2. Erhalten Sie Ihre API Credentials vom TWINT Support<br />
                          3. Konfigurieren Sie Ihre Merchant ID und API Keys<br />
                          4. Testen Sie mit der TWINT Sandbox App
                        </Typography>
                      </Alert>
                    </Grid>
                  )}

                  {/* Save Button */}
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={() => handleSave(gateway)}
                      disabled={saving[gateway.gateway_name]}
                      fullWidth
                    >
                      {saving[gateway.gateway_name] ? 'Speichern...' : 'Einstellungen Speichern'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Important Security Notice */}
      <Alert severity="warning" sx={{ mt: 4 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          ⚠️ Sicherheitshinweise:
        </Typography>
        <Typography variant="caption" component="div">
          • Verwenden Sie im Test Modus NIEMALS echte Kreditkarten<br />
          • Wechseln Sie erst in den Live Modus, wenn Sie vollständig getestet haben<br />
          • Bewahren Sie Ihre Secret Keys sicher auf und teilen Sie sie niemals<br />
          • Live Keys sollten nur auf sicheren Servern gespeichert werden<br />
          • Überprüfen Sie regelmässig Ihre Zahlungstransaktionen
        </Typography>
      </Alert>

      {/* Status Overview */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
            Zahlungsstatus Übersicht
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                  {gateways.filter(g => g.is_active).length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Aktive Gateways
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: gateways.some(g => g.is_active && g.is_test_mode) ? 'warning.main' : 'success.main' }}>
                  {gateways.some(g => g.is_active && g.is_test_mode) ? 'TEST' : 'LIVE'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Aktiver Modus
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: gateways.filter(g => g.is_active).length > 0 ? 'success.main' : 'text.secondary' }}>
                  {gateways.filter(g => g.is_active).length > 0 ? 'EIN' : 'AUS'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Zahlungspflicht
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          {gateways.filter(g => g.is_active).length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Hinweis:</strong> Wenn Zahlungsgateways aktiv sind, müssen Kunden vor der Buchung bezahlen.
                Buchungen ohne erfolgreiche Zahlung werden nicht erstellt.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentSettings;

